import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { FlagGrid, type FlagItem } from '~/components/product/flag-grid';
import { FormField } from '~/components/system/form-field';
import { applyServerValidation, serverMessage } from '~/lib/api/server-error';
import { useUserRoleMutations } from '~/hooks/use-user-roles';
import { APPOINTMENTS_MODULE, useHasSpecialModule } from '~/lib/auth/use-has-special-module';
import { userRoleSchema, EMPTY_USER_ROLE, visibleRolePermissions, type UserRoleFormValues } from '~/lib/schemas/user-role';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	initial: UserRoleFormValues | null;
}

/** Add / edit a user role. Name + sort + visible, plus the 12 permission toggles (FlagGrid). */
export function UserRoleEditDialog({ open, onOpenChange, initial }: Props) {
	const { t } = useTranslation();
	const { create, update } = useUserRoleMutations();
	const form = useForm<UserRoleFormValues>({
		resolver: zodResolver(userRoleSchema) as Resolver<UserRoleFormValues>,
		defaultValues: initial ?? EMPTY_USER_ROLE,
		mode: 'onChange'
	});
	const { register, handleSubmit, reset, setError, setValue, watch, formState } = form;
	const isEdit = Boolean(initial?.id);

	useEffect(() => {
		if (open) reset(initial ?? EMPTY_USER_ROLE);
	}, [open, initial, reset]);

	const onSubmit = handleSubmit(async values => {
		try {
			if (values.id) await update.mutateAsync(values);
			else await create.mutateAsync(values);
			toast.success(t('common.save'));
			onOpenChange(false);
		} catch (err) {
			if (!applyServerValidation(err, setError)) toast.error(serverMessage(err));
		}
	});

	const values = watch();
	const permissions = visibleRolePermissions(useHasSpecialModule(APPOINTMENTS_MODULE));
	const flags: FlagItem[] = permissions.map(p => ({ key: p, label: t(`system.userRoles.perms.${p}`), checked: values[p] }));

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-2xl">
				<DialogHeader>
					<DialogTitle>{isEdit ? t('system.userRoles.editTitle') : t('system.userRoles.addTitle')}</DialogTitle>
					<DialogDescription>{t('system.userRoles.formSubtitle')}</DialogDescription>
				</DialogHeader>
				<form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
					<div className="grid grid-cols-[1fr_120px] gap-4">
						<FormField label={t('system.userRoles.columns.name')} error={formState.errors.nm?.message}>
							<Input {...register('nm')} maxLength={100} aria-invalid={Boolean(formState.errors.nm)} autoFocus />
						</FormField>
						<FormField label={t('system.userRoles.columns.sort')} error={formState.errors.si?.message}>
							<Input type="number" {...register('si', { valueAsNumber: true })} aria-invalid={Boolean(formState.errors.si)} />
						</FormField>
					</div>
					<div className="flex items-center justify-between rounded-md border border-border px-4 py-2.5">
						<Label htmlFor="role-visible">{t('system.userRoles.columns.visible')}</Label>
						<Switch id="role-visible" checked={values.iv} onCheckedChange={next => setValue('iv', next, { shouldDirty: true })} />
					</div>
					<div className="flex flex-col gap-2">
						<span className="text-[11px] font-medium uppercase tracking-[0.04em] text-foreground-subtle">
							{t('system.userRoles.permissions')}
						</span>
						<FlagGrid
							flags={flags}
							columns={2}
							onToggle={(key, checked) => setValue(key as keyof UserRoleFormValues, checked, { shouldDirty: true })}
						/>
					</div>
					<DialogFooter>
						<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
							{t('common.cancel')}
						</Button>
						<Button type="submit" disabled={create.isPending || update.isPending}>
							{create.isPending || update.isPending ? t('common.saving') : t('common.save')}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
